import { Modal, ModalFooter } from '@/components/common';
import { CreateInstitutionRequest } from '@/models';
import React from 'react';
import { InstitutionLogoField } from './InstitutionLogoField';

interface InstitutionFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: CreateInstitutionRequest;
  isSaving: boolean;
  onFieldChange: (field: keyof CreateInstitutionRequest, value: string) => void;
  onInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const InstitutionFormModal: React.FC<InstitutionFormModalProps> = ({
  isOpen,
  mode,
  formData,
  isSaving,
  onFieldChange,
  onInputChange,
  onSubmit,
  onClose,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title={mode === 'create' ? 'Add New Institution' : 'Edit Institution'}
      size="lg"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={onSubmit}
          confirmText={mode === 'create' ? 'Create' : 'Update'}
          isLoading={isSaving}
          disabled={!formData.name.trim()}
        />
      }
    >
      <form>
        <div className="row g-3">
          {/* Basic Info */}
          <div className="col-12">
            <label htmlFor="name" className="form-label">
              Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={formData.name}
              onChange={onInputChange}
              maxLength={200}
              required
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="contact" className="form-label">
              Contact Person
            </label>
            <input
              type="text"
              className="form-control"
              id="contact"
              name="contact"
              value={formData.contact || ''}
              onChange={onInputChange}
              maxLength={200}
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="accountNumber" className="form-label">
              Account Number
            </label>
            <input
              type="text"
              className="form-control"
              id="accountNumber"
              name="accountNumber"
              value={formData.accountNumber || ''}
              onChange={onInputChange}
              maxLength={100}
            />
          </div>

          {/* Address */}
          <div className="col-12">
            <label htmlFor="address" className="form-label">
              Address
            </label>
            <input
              type="text"
              className="form-control"
              id="address"
              name="address"
              value={formData.address || ''}
              onChange={onInputChange}
              maxLength={500}
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="city" className="form-label">
              City
            </label>
            <input
              type="text"
              className="form-control"
              id="city"
              name="city"
              value={formData.city || ''}
              onChange={onInputChange}
              maxLength={100}
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="province" className="form-label">
              Province
            </label>
            <input
              type="text"
              className="form-control"
              id="province"
              name="province"
              value={formData.province || ''}
              onChange={onInputChange}
              maxLength={100}
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="postalCode" className="form-label">
              Postal Code
            </label>
            <input
              type="text"
              className="form-control"
              id="postalCode"
              name="postalCode"
              value={formData.postalCode || ''}
              onChange={onInputChange}
              maxLength={20}
            />
          </div>

          {/* Contact Info */}
          <div className="col-md-6">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              type="email"
              className="form-control"
              id="email"
              name="email"
              value={formData.email || ''}
              onChange={onInputChange}
              maxLength={200}
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="phoneNumber" className="form-label">
              Phone Number
            </label>
            <input
              type="tel"
              className="form-control"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber || ''}
              onChange={onInputChange}
              maxLength={50}
            />
          </div>

          {/* Additional */}
          <div className="col-12">
            <label htmlFor="safetyVideoUrl" className="form-label">
              Safety Video URL
            </label>
            <input
              type="url"
              className="form-control"
              id="safetyVideoUrl"
              name="safetyVideoUrl"
              value={formData.safetyVideoUrl || ''}
              onChange={onInputChange}
              maxLength={500}
            />
          </div>

          <div className="col-md-6">
            <InstitutionLogoField
              name="logoIconUrl"
              label="Logo Icon"
              mode={mode}
              value={formData.logoIconUrl}
              helperText="Drop or choose a compact icon image, or paste a remote image URL."
              onChange={onFieldChange}
            />
          </div>

          <div className="col-md-6">
            <InstitutionLogoField
              name="logoFullUrl"
              label="Full Logo"
              mode={mode}
              value={formData.logoFullUrl}
              helperText="Drop or choose the full-width brand logo, or paste a remote image URL."
              onChange={onFieldChange}
            />
          </div>

          <div className="col-12">
            <div className="border rounded-3 p-3">
              <div className="mb-3">
                <h6 className="mb-1">Emergency Contact</h6>
                <p className="text-muted small mb-0">
                  Configure the contact details shown for emergency assistance.
                </p>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="emergencyContactName" className="form-label">
                    Emergency Contact Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="emergencyContactName"
                    name="emergencyContactName"
                    value={formData.emergencyContactName || ''}
                    onChange={onInputChange}
                    maxLength={200}
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="emergencyContactPhone" className="form-label">
                    Emergency Contact Phone
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    id="emergencyContactPhone"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone || ''}
                    onChange={onInputChange}
                    maxLength={50}
                  />
                </div>

                <div className="col-12">
                  <label htmlFor="emergencyContactEmail" className="form-label">
                    Emergency Contact Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="emergencyContactEmail"
                    name="emergencyContactEmail"
                    value={formData.emergencyContactEmail || ''}
                    onChange={onInputChange}
                    maxLength={200}
                  />
                </div>

                <div className="col-12">
                  <label htmlFor="emergencyContactMessage" className="form-label">
                    Emergency Contact Message
                  </label>
                  <textarea
                    className="form-control"
                    id="emergencyContactMessage"
                    name="emergencyContactMessage"
                    rows={3}
                    value={formData.emergencyContactMessage || ''}
                    onChange={onInputChange}
                    maxLength={1000}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <label htmlFor="certificationConfiguration" className="form-label">
              Certification Configuration
            </label>
            <textarea
              className="form-control"
              id="certificationConfiguration"
              name="certificationConfiguration"
              rows={3}
              value={formData.certificationConfiguration || ''}
              onChange={onInputChange}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};
